import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'styles/util';
import strings from 'localization/strings';
import { fetchStorageGroupBookingCalendar } from 'actions/storageGroups';
import { handleResponse } from 'actions/actionHelpers';
import { enrichStorages } from 'logic/bookingLogic';
import bookingStatuses from 'enums/bookingStatuses';
import { getLongDateFormat, formatIsoDate, parseDate } from 'helpers/DateHelper';
import { formatStorageListItem } from 'helpers/StorageSiteHelper';
import { add, sub, startOfDay } from 'date-fns';
import { required } from 'form/validation';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Form from 'form/Form';
import { TextField, showErrorOnBlur } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Loader from 'common/Loader';
import LoadingError from 'common/LoadingError';
import DatePicker from 'form/DatePicker';

const useStyles = makeStyles(({ theme, colors }) => ({
    loader: {
        marginTop: theme.spacing(2)
    },
    notAvailable: {
        color: colors.textColorDisabled
    }
}));

const ChangeStorageDialog = ({ booking, open, onOk, onCancel }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { appContext } = useAppContext();

    const today = startOfDay(new Date());
    let firstAvailableStartDate = parseDate(booking.startDate, appContext);
    if(today > firstAvailableStartDate) {
        firstAvailableStartDate = today;
    }
    const showStartDateField = booking.bookingStatus === bookingStatuses.purchased.key;

    const initialValues = {
        storageId: booking.storage
            ? booking.storage.id
            : undefined,
        startDate: showStartDateField
            ? firstAvailableStartDate
            : undefined
    };

    const [bookingCalendar, setBookingCalendar] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingError, setLoadingError] = useState(undefined);

    useEffect(() => {
        if(open) {
            setIsLoading(true);
            dispatch(fetchStorageGroupBookingCalendar(booking.storageGroup.id, booking.startDate, booking.id))
                .then(handleResponse(
                    response => {
                        setBookingCalendar(response.payload);
                        setIsLoading(false);
                    },
                    () => {
                        setLoadingError(true);
                        setIsLoading(false);
                    }
                ));
        }
    }, [open]);

    const storages = bookingCalendar
        ? enrichStorages(booking.storageGroup, bookingCalendar, booking.startDate, booking.endDate, booking.storage.id)
        : undefined;

    const handleFormSubmit = values => {
        onOk(values);
    };

    const storageHasChanged = formValues => formValues.storageId !== initialValues.storageId;

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={onCancel}>
            <Form
                initialValues={initialValues}
                onSubmit={handleFormSubmit}
            >
                {({ handleSubmit, values }) => (
                    <form onSubmit={handleSubmit}>
                        <DialogTitle disableTypography>
                            <Typography variant="h5">
                                {strings.changeStorageDialogTitle}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body1" gutterBottom>
                                {strings.changeStorageDialogContent}
                            </Typography>
                            { isLoading && <Loader /> }
                            { loadingError && <LoadingError /> }
                            {
                                showStartDateField &&
                                (
                                    <DatePicker
                                        name="startDate"
                                        label={strings.storageChangeDate}
                                        formatFunc={getLongDateFormat}
                                        inputVariant="outlined"
                                        showError={showErrorOnBlur}
                                        required
                                        fieldProps={{
                                            validate: required,
                                            minDate: formatIsoDate(firstAvailableStartDate, appContext),
                                            maxDate: formatIsoDate(sub(add(today, { months: 1 }), { days: 1 }), appContext) }}
                                        autoFocus
                                    />
                                )
                            }
                            {
                                storages &&
                                (
                                    <TextField
                                        select
                                        name="storageId"
                                        label={strings.selectStorageLabel}
                                        variant="outlined"
                                        autoFocus
                                    >
                                        {
                                            storages.map(storage => (
                                                <MenuItem key={storage.id} value={storage.id}>
                                                    {formatStorageListItem(storage, classes.notAvailable)}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                )
                            }
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isLoading || loadingError || !storageHasChanged(values)}
                            >
                                {strings.save}
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onCancel}
                            >
                                {strings.cancel}
                            </Button>
                        </DialogActions>
                    </form>
                )}
            </Form>
        </Dialog>
    );
};

ChangeStorageDialog.propTypes = {
    booking: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onOk: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default ChangeStorageDialog;
